import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Refund Dialog — asks for a manager PIN, an amount, and an optional
 * reason before refunding all or part of an already-paid order.
 * Extracted from POSPage.jsx (~45 lines inline). The actual
 * ``handleRefund`` call still lives on POSPage (it needs access to
 * selectedOrder, refundAmount, refundPin/Reason, and reload logic).
 */
const RefundDialog = ({
  open, onOpenChange,
  selectedOrder,
  paymentInfo,
  refundAmount, setRefundAmount,
  refundPin, setRefundPin,
  refundReason, setRefundReason,
  refundLoading,
  onSubmit,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[400px]" data-testid="refund-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl">Issue Refund</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div className="bg-secondary/50 rounded-xl p-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Order</span>
            <span className="font-medium">{selectedOrder?.table_name || "Quick Order"}</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-muted-foreground">Order Total</span>
            <span className="font-mono font-medium">${(selectedOrder?.total || 0).toFixed(2)}</span>
          </div>
          {paymentInfo?.payment_method && paymentInfo.payment_method !== "unknown" && (
            <div className="flex justify-between text-sm mt-1">
              <span className="text-muted-foreground">Paid by</span>
              <span className="capitalize font-medium">
                {paymentInfo.payment_method === "card"
                  ? "Credit Card"
                  : paymentInfo.payment_method === "cash"
                  ? "Cash"
                  : paymentInfo.payment_method}
              </span>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label>Refund Amount ($)</Label>
          <Input
            type="number"
            step="0.01"
            min="0.01"
            max={selectedOrder?.total || 0}
            value={refundAmount}
            onChange={(e) => setRefundAmount(e.target.value)}
            className="font-mono text-lg"
            data-testid="refund-amount"
          />
          <p className="text-xs text-muted-foreground">Full refund: ${(selectedOrder?.total || 0).toFixed(2)}</p>
        </div>
        <div className="space-y-2">
          <Label>Manager PIN (required)</Label>
          <Input
            type="password"
            maxLength={4}
            placeholder="****"
            value={refundPin}
            onChange={(e) => setRefundPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            className="font-mono text-center text-xl tracking-[8px]"
            data-testid="refund-pin"
          />
        </div>
        <div className="space-y-2">
          <Label>Reason (optional)</Label>
          <Input
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder="e.g. Customer complaint, wrong order..."
            data-testid="refund-reason"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button
          variant="destructive"
          onClick={onSubmit}
          disabled={refundPin.length !== 4 || refundLoading}
          data-testid="confirm-refund-btn"
        >
          {refundLoading ? "Processing..." : "Process Refund"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default RefundDialog;
